import { FC } from 'react';
import { useAppSelector } from '../../../../lib/store/hooks';
import { Form } from 'antd';
import {
  formatArrangedData,
  getArrangesInitialValues,
  getParsedSelectedItems,
  getSelectedItemsLabels,
} from '../../../../lib/utils/arranges-helpers';
import { useStringArrange } from '../../../../features/table/helpers/useStringArrange';
import { TableHeaderCell } from '../../../../components/Table/TableHeaderCell/TableHeaderCell';
import { ArrangeListPopup } from '../../../../features/table/components/popups/ArrangeListPopup';
import {
  useLazyGetProjectsForFilteringQuery,
  useLazyGetTemplatesQuery,
} from '../../../../lib/store/api/project';
import { useProjectArrangeRequest } from '../../hooks/useProjectArrangeRequest';
import { useParams } from 'react-router-dom';
import { PROJECT_COLUMN } from '../../helpers/constants';

type Props = {
  arrangedId: string;
  title: string;
  searchFieldName: string;
  filterFieldName: string;
  // TODO check if needed
  headerCellProps: any;
  isParsedByValue?: boolean;
  disableSorting?: boolean;
};

export const ProjectSearchDataHeaderCell: FC<Props> = ({
  arrangedId,
  title,
  searchFieldName,
  filterFieldName,
  headerCellProps: additionalHeaderCellProps,
  isParsedByValue,
  disableSorting,
}) => {
  const sortingAndFiltersData = useAppSelector(({ projects }) => projects);

  const {
    arrangeId,
    sorting,
    filtering: { filter },
  } = sortingAndFiltersData;

  const [findProjects] = useLazyGetProjectsForFilteringQuery();
  const [findTemplates] = useLazyGetTemplatesQuery();

  const { id: tenantId } = useParams();

  const isActiveArrange = arrangeId === arrangedId;

  const [form] = Form.useForm();
  const getSelectedItems = () => getSelectedItemsLabels(form);

  const { filtering } = useStringArrange({
    form,
    request: (value: string) => {
      if (arrangedId === PROJECT_COLUMN.USED_TEMPLATE) {
        return findTemplates({ tenantId: String(tenantId), body: { searchByName: value } }).unwrap();
      }
      return findProjects({ search: value, searchFieldName, tenantId: String(tenantId) }).unwrap();
    },
    formatResponse: (data: { content: any[] }) => {
      if (arrangedId === PROJECT_COLUMN.USED_TEMPLATE) {
        return data?.content.map((item) => ({
          label: item.name,
          value: JSON.stringify({
            value: item.id,
            label: item.name,
          }),
        }));
      }

      return formatArrangedData(data, arrangedId);
    },
  });

  const { applyArranges, removeFilter, removeFilterValue } = useProjectArrangeRequest({
    arrangedId,
    getFilterLabel: () => getSelectedItems().join(', '),
    getFilters: () => ({ [filterFieldName]: getSelectedItems() }),
    form,
    filterFieldName,
    isParsedByValue,
  });

  const headerCellProps = {
    arrangedId,
    title,
    removeFilter,
    filterLabel: Array.isArray(filter) && filter.join(', '),
    sortOrder: sorting.sortOrder,
    isActiveArrange,
    form,
    applyArranges,
    getParsedItems: () => getParsedSelectedItems(form),
    removeFilterValue,
    ...additionalHeaderCellProps,
  };

  return (
    <TableHeaderCell {...headerCellProps}>
      <ArrangeListPopup
        disableSorting={disableSorting}
        initialValues={getArrangesInitialValues(form)}
        form={form}
        filtering={filtering}
        onFinish={applyArranges}
        isActiveArrange={isActiveArrange}
      />
    </TableHeaderCell>
  );
};
